import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  IconButton,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
} from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { AllocationForm } from '@/hooks/useContractorManagement';
import financeClientsService from '@/services/financeClients';
import { ContractorReconciliation, ContractorRow, ManagedContractor, TeamOption } from '@/services/financeContractors';
import { WarpWorker } from '@/services/financeWarp';
import { DatePreset, DateFilter } from './FinanceXContractors.container';

type AvailableProfile = { id: string; firstName: string; lastName: string; email: string; developerType: string | null };

type ManagementProps = {
  managedContractors: ManagedContractor[];
  loadingContractors: boolean;
  teams: TeamOption[];
  editDialog: AllocationForm | null;
  saving: boolean;
  openAdd: (profileId?: string) => void;
  openEdit: (c: ManagedContractor) => void;
  closeDialog: () => void;
  handleSave: (form: AllocationForm) => Promise<void>;
  handleRemove: (allocationId: string) => Promise<void>;
};

type Props = {
  recon: ContractorReconciliation;
  isLoading: boolean;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
  onLinkVendor: (vendorId: string, profileId: string) => Promise<void>;
  management: ManagementProps;
  warpConnected: boolean;
  warpContractors: WarpWorker[];
  isWarpSyncing: boolean;
  isWarpLinking: boolean;
  warpMessage: string | null;
  warpCreateProfiles: boolean;
  onWarpCreateProfilesChange: (value: boolean) => void;
  onWarpSync: () => void;
  onWarpLink: () => void;
};

const warpName = (w: WarpWorker): string =>
  w.displayName || [w.firstName, w.lastName].filter(Boolean).join(' ') || w.businessName || '—';

/** Link status for a synced Warp contractor. */
function WarpRosterBadge({ worker }: { worker: WarpWorker }): JSX.Element {
  if (worker.vendorId) {
    return <Chip label="Vendor" size="small" color="success" variant="outlined" sx={{ fontSize: 10, height: 20 }} />;
  }
  if (worker.profileId) {
    return <Chip label="Profile only" size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />;
  }
  return <Chip label="Unlinked" size="small" color="warning" variant="outlined" sx={{ fontSize: 10, height: 20 }} />;
}

const fmt = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

const fmtRate = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

function getVarianceIcon(c: ContractorRow): JSX.Element {
  if (c.totalActualPaid === 0 && c.totalBudgeted > 0) {
    return <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: 18 }} />;
  }
  if (c.variance <= 0) {
    return <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 18 }} />;
  }
  return <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 18 }} />;
}

function getButtonLabel(saving: boolean, isEdit: boolean): string {
  if (saving) return 'Saving...';
  return isEdit ? 'Update' : 'Add';
}

function ContractorRowComponent({ contractor }: { contractor: ContractorRow }): JSX.Element {
  const c = contractor;
  const varianceColor = c.variance <= 0 ? '#10B981' : '#EF4444';
  const clients = c.allocations.map((a) => `${a.teamName} (${a.allocationPct}%)`).join(', ');

  return (
    <tr style={{ borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
        {c.firstName} {c.lastName}
      </td>
      <td style={{ padding: '10px 12px', fontSize: 13, color: '#4A4B4E' }}>
        {clients || <span style={{ color: '#D4D4D8' }}>No allocations</span>}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmt(c.totalBudgeted)}</td>
      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
        {c.totalActualPaid > 0 ? fmt(c.totalActualPaid) : <span style={{ color: '#F59E0B' }}>$0</span>}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500, color: varianceColor }}>
        {fmt(c.variance)}
      </td>
      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
        {getVarianceIcon(c)}
      </td>
    </tr>
  );
}

function AllocationDialog({
  open,
  form,
  teams,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  form: AllocationForm | null;
  teams: TeamOption[];
  saving: boolean;
  onClose: () => void;
  onSave: (form: AllocationForm) => Promise<void>;
}): JSX.Element {
  const [local, setLocal] = useState<AllocationForm | null>(null);
  const [searchResults, setSearchResults] = useState<AvailableProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<AvailableProfile | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const active = local ?? form;
  const isEdit = !!active?.allocationId;

  const handleOpen = (): void => {
    if (form && !local) setLocal({ ...form });
    setSearchResults([]);
    setSelectedProfile(null);
  };

  const update = (field: keyof AllocationForm, value: string): void => {
    if (!active) return;
    setLocal({ ...active, [field]: value });
  };

  const handleSearchChange = async (_: any, value: string): Promise<void> => {
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const results = await financeClientsService.searchProfiles(value);
      setSearchResults(results);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { setLocal(null); setSelectedProfile(null); onClose(); }}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleOpen }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Edit Contractor Rate' : 'Add Contractor'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Autocomplete
          options={searchResults}
          getOptionLabel={(p) => `${p.firstName} ${p.lastName} (${p.email})`}
          value={selectedProfile}
          onChange={(_, val) => {
            setSelectedProfile(val);
            update('profileId', val?.id ?? '');
          }}
          onInputChange={handleSearchChange}
          disabled={isEdit}
          loading={searchLoading}
          noOptionsText="Type at least 2 characters to search..."
          renderInput={(params) => (
            <TextField {...params} label="Search contractor by name..." size="small" fullWidth />
          )}
          filterOptions={(x) => x}
          sx={{ mt: 1 }}
        />
        <TextField
          select
          label="Team"
          size="small"
          value={active?.teamId ?? ''}
          onChange={(e) => update('teamId', e.target.value)}
          fullWidth
          helperText={teams.length === 0 ? 'No teams found. Create a team first in Client Profitability.' : undefined}
          error={teams.length === 0}
          disabled={teams.length === 0}
        >
          {teams.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Monthly Rate ($)"
            size="small"
            type="number"
            value={active?.monthlyRate ?? ''}
            onChange={(e) => update('monthlyRate', e.target.value)}
            fullWidth
          />
          <TextField
            label="Allocation %"
            size="small"
            type="number"
            value={active?.allocationPct ?? ''}
            onChange={(e) => update('allocationPct', e.target.value)}
            sx={{ maxWidth: 120 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start Date"
            size="small"
            type="date"
            value={active?.startDate ?? ''}
            onChange={(e) => update('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            size="small"
            type="date"
            value={active?.endDate ?? ''}
            onChange={(e) => update('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {!isEdit && (!active?.profileId || !active?.teamId || !active?.monthlyRate) && (
          <Box sx={{ fontSize: 12, color: '#71717A', textAlign: 'center' }}>
            {[
              !active?.profileId && 'contractor',
              !active?.teamId && 'team',
              !active?.monthlyRate && 'monthly rate',
            ].filter(Boolean).join(', ')} required
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => { setLocal(null); onClose(); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!active?.profileId || !active?.teamId || !active?.monthlyRate || saving}
            onClick={() => active && onSave(active).then(() => setLocal(null)).catch(() => {})}
            sx={{ textTransform: 'none', backgroundColor: '#054AD9' }}
          >
            {getButtonLabel(saving, isEdit)}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const FinanceXContractorsComponent = function ({
  recon,
  isLoading,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
  onLinkVendor,
  management,
  warpConnected,
  warpContractors,
  isWarpSyncing,
  isWarpLinking,
  warpMessage,
  warpCreateProfiles,
  onWarpCreateProfilesChange,
  onWarpSync,
  onWarpLink,
}: Props): JSX.Element {
  const { isMobile } = useBreakpoint();
  const warpUnlinkedCount = warpContractors.filter((w) => !w.vendorId).length;
  const [linkDialog, setLinkDialog] = useState<{ vendorId: string; vendorName: string } | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<AvailableProfile | null>(null);
  const [linkSearchResults, setLinkSearchResults] = useState<AvailableProfile[]>([]);
  const [linkSearchLoading, setLinkSearchLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  if (isLoading) return <LoadingState message="Loading contractor data..." />;

  const { contractors, totalBudgeted, totalActualPaid, totalVariance, unlinkedPayments } = recon;

  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none', fontSize: 13, px: 1.5, py: 0.5, borderColor: '#E4E4E7',
            '&.Mui-selected': { backgroundColor: '#054AD915', color: '#054AD9', borderColor: '#054AD9', '&:hover': { backgroundColor: '#054AD925' } },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField type="date" size="small" value={dateFilter.startDate} onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)} sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }} />
          <TextField type="date" size="small" value={dateFilter.endDate} onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)} sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }} />
        </Box>
      )}
    </Box>
  );

  const handleLink = async (): Promise<void> => {
    if (!linkDialog || !selectedProfile) return;
    setLinking(true);
    try {
      await onLinkVendor(linkDialog.vendorId, selectedProfile.id);
      setLinkDialog(null);
      setSelectedProfile(null);
    } finally {
      setLinking(false);
    }
  };

  const totalMonthlyForecast = management.managedContractors.reduce(
    (sum, c) => sum + c.monthlyRate * (c.allocationPct / 100), 0
  );

  return (
    <>
      <PageHeader title="Contractors" actions={dateFilterActions} />

      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        <MetricCard label="Active Contractors" value={String(management.managedContractors.length)} color="#054AD9" />
        <MetricCard label="Monthly Forecast" value={fmt(totalMonthlyForecast)} color="#054AD9" />
        <MetricCard label="Total Paid (Period)" value={fmt(totalActualPaid)} color={totalActualPaid <= totalBudgeted ? '#10B981' : '#EF4444'} />
        <MetricCard
          label="Variance"
          value={fmt(totalVariance)}
          color={totalVariance <= 0 ? '#10B981' : '#EF4444'}
        />
      </Box>

      {/* Unlinked Payments Warning */}
      {unlinkedPayments.length > 0 && (
        <Box sx={{ mb: 3, border: '2px solid #FDE68A', borderRadius: '12px', backgroundColor: '#FFFBEB', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
            <Box sx={{ fontSize: 14, fontWeight: 700, color: '#92400E' }}>
              Unlinked Payroll Payments
            </Box>
          </Box>
          <Box sx={{ fontSize: 13, color: '#78350F', mb: 1.5 }}>
            These vendors have payroll-category payments but aren&apos;t linked to a team member profile. Link them to enable budget vs actual tracking.
          </Box>
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #FDE68A' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 500, color: '#92400E' }}>Vendor</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500, color: '#92400E' }}>Payments</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500, color: '#92400E' }}>Total Paid</th>
                  <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 500, color: '#92400E' }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {unlinkedPayments.map((up) => (
                  <tr key={up.vendorId} style={{ borderBottom: '1px solid #FEF3C7' }}>
                    <td style={{ padding: '6px 10px', fontWeight: 500 }}>{up.vendorName}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{up.txCount}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>{fmt(up.totalPaid)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                      <Button
                        size="small"
                        startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setLinkDialog({ vendorId: up.vendorId, vendorName: up.vendorName })}
                        sx={{ textTransform: 'none', fontSize: 12, color: '#054AD9' }}
                      >
                        Link
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Warp synced contractors */}
      {warpConnected && (
        <Box sx={{ mb: 3 }}>
          <SectionCard
            title="Warp Contractors"
            action={
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  startIcon={<SyncIcon sx={{ fontSize: 16 }} />}
                  disabled={isWarpSyncing}
                  onClick={onWarpSync}
                  sx={{ textTransform: 'none', fontSize: 13, color: '#054AD9' }}
                >
                  {isWarpSyncing ? 'Syncing...' : 'Sync from Warp'}
                </Button>
                <Button
                  size="small"
                  startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                  disabled={isWarpLinking || warpUnlinkedCount === 0}
                  onClick={onWarpLink}
                  sx={{ textTransform: 'none', fontSize: 13, color: '#054AD9' }}
                >
                  {isWarpLinking ? 'Linking...' : `Link to Vendors${warpUnlinkedCount ? ` (${warpUnlinkedCount})` : ''}`}
                </Button>
              </Box>
            }
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={warpCreateProfiles}
                  onChange={(e) => onWarpCreateProfilesChange(e.target.checked)}
                />
              }
              label="Create profiles for new people not found by personal or work email"
              sx={{ display: 'block', mb: 1, '& .MuiFormControlLabel-label': { fontSize: 13, color: '#4A4B4E' } }}
            />
            {warpMessage && (
              <Box sx={{ fontSize: 12, mb: 1.5, color: warpMessage.startsWith('Error') ? '#EF4444' : '#10B981' }}>
                {warpMessage}
              </Box>
            )}
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #F4F4F5' }}>
                    {['Contractor', 'Position', 'Status', 'Department', 'Linked', ''].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '10px 12px',
                          textAlign: h === 'Linked' || h === '' ? 'center' : 'left',
                          fontWeight: 500,
                          fontSize: 12,
                          color: '#71717A',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {warpContractors.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: '40px 12px', textAlign: 'center', color: '#71717A', fontSize: 14 }}>
                        No Warp contractors synced yet. Click &quot;Sync from Warp&quot; to pull the roster.
                      </td>
                    </tr>
                  )}
                  {warpContractors.map((w) => (
                    <tr key={w.id} style={{ borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{warpName(w)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: '#4A4B4E' }}>{w.position || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: '#4A4B4E' }}>{w.status || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: '#4A4B4E' }}>{w.departmentName || '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <WarpRosterBadge worker={w} />
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {w.profileId && (
                          <Button
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={() => management.openAdd(w.profileId ?? undefined)}
                            sx={{ textTransform: 'none', fontSize: 12, color: '#054AD9' }}
                          >
                            Add rate
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {warpUnlinkedCount > 0 && (
              <Box sx={{ fontSize: 12, color: '#71717A', mt: 1.5 }}>
                {warpUnlinkedCount} contractor{warpUnlinkedCount === 1 ? '' : 's'} not yet linked to a vendor. Click
                &nbsp;<strong>Link to Vendors</strong>&nbsp;to create vendor records for budget vs actual tracking.
              </Box>
            )}
          </SectionCard>
        </Box>
      )}

      {/* Manage Contractor Rates */}
      <SectionCard
        title="Contractor Rates"
        action={
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => management.openAdd()}
            sx={{ textTransform: 'none', fontSize: 13, color: '#054AD9' }}
          >
            Add Contractor
          </Button>
        }
      >
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #F4F4F5' }}>
                {['Contractor', 'Team', 'Monthly Rate', 'Allocation', 'Effective Cost', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Contractor' || h === 'Team' ? 'left' : 'right', fontWeight: 500, fontSize: 12, color: '#71717A', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {management.managedContractors.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '40px 12px', textAlign: 'center', color: '#71717A', fontSize: 14 }}>
                    No contractor rates configured yet. Add contractors to forecast monthly expenses.
                  </td>
                </tr>
              )}
              {management.managedContractors.map((c) => (
                <tr key={c.allocationId} style={{ borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                    {c.firstName} {c.lastName}
                    {c.monthlyRate === 0 && (
                      <Box component="span" sx={{ ml: 1, fontSize: 11, color: '#EF4444', fontWeight: 400 }}>Missing rate</Box>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: '#4A4B4E' }}>{c.teamName}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {c.monthlyRate > 0 ? fmtRate(c.monthlyRate) : <span style={{ color: '#EF4444' }}>$0.00</span>}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{c.allocationPct}%</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500 }}>
                    {fmtRate(c.monthlyRate * (c.allocationPct / 100))}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => management.openEdit(c)} sx={{ color: '#71717A' }}>
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => { if (window.confirm('Remove this contractor rate?')) management.handleRemove(c.allocationId); }} sx={{ color: '#71717A' }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </td>
                </tr>
              ))}
              {management.managedContractors.length > 0 && (
                <tr style={{ borderTop: '2px solid #09090B', fontWeight: 700, backgroundColor: '#FAFAFA' }}>
                  <td style={{ padding: '12px', fontWeight: 700 }}>Total Monthly Forecast</td>
                  <td style={{ padding: '12px' }} />
                  <td style={{ padding: '12px' }} />
                  <td style={{ padding: '12px' }} />
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>{fmtRate(totalMonthlyForecast)}</td>
                  <td style={{ padding: '12px' }} />
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </SectionCard>

      {/* Budget vs Actual Table */}
      <Box sx={{ mt: 3 }}>
        <SectionCard title="Budget vs Actual">
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #F4F4F5' }}>
                  {['Contractor', 'Client(s)', 'Budgeted', 'Actual Paid', 'Variance', ''].map((h) => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Contractor' || h === 'Client(s)' ? 'left' : 'right', fontWeight: 500, fontSize: 12, color: '#71717A', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contractors.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: '40px 12px', textAlign: 'center', color: '#71717A', fontSize: 14 }}>
                      No contractors linked yet. Link vendor payments to team member profiles to start tracking.
                    </td>
                  </tr>
                )}
                {contractors.map((c) => (
                  <ContractorRowComponent key={c.profileId} contractor={c} />
                ))}
                {contractors.length > 0 && (
                  <tr style={{ borderTop: '2px solid #09090B', fontWeight: 700, backgroundColor: '#FAFAFA' }}>
                    <td style={{ padding: '12px', fontWeight: 700 }}>Total</td>
                    <td style={{ padding: '12px' }} />
                    <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(totalBudgeted)}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>{fmt(totalActualPaid)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: totalVariance <= 0 ? '#10B981' : '#EF4444' }}>
                      {fmt(totalVariance)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {totalVariance <= 0
                        ? <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 18 }} />
                        : <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 18 }} />
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </SectionCard>
      </Box>

      {/* Link Dialog */}
      <Dialog open={!!linkDialog} onClose={() => { setLinkDialog(null); setSelectedProfile(null); setLinkSearchResults([]); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Link &quot;{linkDialog?.vendorName}&quot; to a team member
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Autocomplete
            options={linkSearchResults}
            getOptionLabel={(p) => `${p.firstName} ${p.lastName} (${p.email})`}
            value={selectedProfile}
            onChange={(_, val) => setSelectedProfile(val)}
            onInputChange={async (_, value) => {
              if (value.length < 2) { setLinkSearchResults([]); return; }
              setLinkSearchLoading(true);
              try {
                const results = await financeClientsService.searchProfiles(value);
                setLinkSearchResults(results);
              } finally { setLinkSearchLoading(false); }
            }}
            loading={linkSearchLoading}
            noOptionsText="Type at least 2 characters to search..."
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Search team member by name..." size="small" fullWidth />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setLinkDialog(null); setSelectedProfile(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={!selectedProfile || linking} onClick={handleLink} sx={{ textTransform: 'none', backgroundColor: '#054AD9' }}>
            {linking ? 'Linking...' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Allocation Dialog */}
      <AllocationDialog
        open={!!management.editDialog}
        form={management.editDialog}
        teams={management.teams}
        saving={management.saving}
        onClose={management.closeDialog}
        onSave={management.handleSave}
      />
    </>
  );
};

export default FinanceXContractorsComponent;
